import { FieldDef, CategoryDef, FieldType } from '../components/SchemaForm/types';
import { TenantAttrDefinition } from '../api/tenantAttrDefinitionApi';

import { tenantAttrSchema } from './tenantAttrSchema';
import { tenantAttrCategories } from './tenantAttrCategories';

/**
 * Merge DB-backed tenant-attribute definitions (config.tenant_attr_definition,
 * mig 280) into the hard-coded code schema.
 *
 * RULES
 *  1. Code wins. A definition whose attrKey already exists in `tenantAttrSchema`
 *     is dropped — two form fields writing the same JSONB path would fight each
 *     other. `skipped` reports these so the definitions UI can flag them.
 *  2. Duplicate attrKeys inside the definition list: first (by group/order/label,
 *     the server's sort) wins. The DB's UNIQUE(attr_key) makes this defensive only.
 *  3. Inactive definitions are ignored (the server already filters them, but the
 *     merge is the last line of defence and is used directly in tests).
 *  4. Unknown fieldType → 'json'. A JSON editor renders any value losslessly,
 *     where a text input would stringify an object and corrupt it on save.
 *  5. A definition group that has no category gets one appended, otherwise
 *     SchemaFormPanel (which renders only groups present in `categories`) would
 *     hide the field entirely.
 */

/** Field types the renderer knows about — keep in sync with `fieldRegistry`. */
const KNOWN_FIELD_TYPES: readonly FieldType[] = [
  'color', 'toggle', 'text', 'number', 'url', 'select', 'json', 'textarea',
  'array', 'date', 'icon-map', 'menu-list', 'menu-icon-map', 'subsystem-version',
];

export const isKnownFieldType = (t: string): t is FieldType =>
  (KNOWN_FIELD_TYPES as readonly string[]).includes(t);

export interface MergedTenantAttrSchema {
  /** Code schema + DB-defined fields, in render order. */
  schema: FieldDef[];
  /** Code categories + one per DB-only group, so every field is reachable. */
  categories: CategoryDef[];
  /** Definitions dropped because the code schema already owns that key. */
  skipped: TenantAttrDefinition[];
}

export function definitionToFieldDef(def: TenantAttrDefinition): FieldDef {
  const field: FieldDef = {
    key: def.attrKey,
    label: def.label || def.attrKey,
    type: isKnownFieldType(def.fieldType) ? def.fieldType : 'json',
    group: def.group || 'Feature Flags',
  };
  if (def.description) field.description = def.description;
  if (def.subGroup) field.subGroup = def.subGroup;
  if (def.options && def.options.length > 0) field.options = def.options;
  if (def.placeholder) field.placeholder = def.placeholder;
  if (def.required) field.required = true;
  if (def.dependsOnKey) field.dependsOn = { key: def.dependsOnKey, value: def.dependsOnValue };
  return field;
}

export function mergeTenantAttrSchema(
  definitions: TenantAttrDefinition[] | undefined,
  baseSchema: FieldDef[] = tenantAttrSchema,
  baseCategories: CategoryDef[] = tenantAttrCategories,
): MergedTenantAttrSchema {
  if (!definitions || definitions.length === 0) {
    return { schema: baseSchema, categories: baseCategories, skipped: [] };
  }

  const codeKeys = new Set(baseSchema.map((f) => f.key));
  const seen = new Set<string>();
  const skipped: TenantAttrDefinition[] = [];
  const extra: FieldDef[] = [];

  for (const def of definitions) {
    if (def.active === false) continue;
    if (codeKeys.has(def.attrKey)) { skipped.push(def); continue; }
    if (seen.has(def.attrKey)) continue;
    seen.add(def.attrKey);
    extra.push(definitionToFieldDef(def));
  }

  if (extra.length === 0) {
    return { schema: baseSchema, categories: baseCategories, skipped };
  }

  const knownGroups = new Set(baseCategories.map((c) => c.key));
  const categories = [...baseCategories];
  for (const f of extra) {
    if (knownGroups.has(f.group)) continue;
    knownGroups.add(f.group);
    categories.push({
      key: f.group,
      label: f.group,
      icon: 'Settings',
      description: 'Custom attribute group defined in the portal (Attribute Definitions).',
    });
  }

  return { schema: [...baseSchema, ...extra], categories, skipped };
}

export default mergeTenantAttrSchema;
